/**
 * The theme, as a tiny external store.
 *
 * `<html data-theme>` is the single source of truth, because the boot script
 * in `index.html` has already set it before React mounts and this module must
 * agree with whatever it decided. Nothing here reads `prefers-color-scheme`:
 * that happens once, in the boot script, and only when no explicit choice has
 * been stored.
 */
export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "savi.theme";

const listeners = new Set<(theme: Theme) => void>();

export function getTheme(): Theme {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export function setTheme(next: Theme): void {
  if (getTheme() === next) return;
  document.documentElement.setAttribute("data-theme", next);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Safari in private mode throws on setItem. A theme that does not
    // persist beats a page that does not render.
  }
  for (const fn of listeners) fn(next);
}

/** Returns its own unsubscribe, so it drops straight into an effect. */
export function subscribeTheme(fn: (theme: Theme) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
